/**
 * @module cercaliagl/SimpleLabel
 */
var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (Object.prototype.hasOwnProperty.call(b, p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        if (typeof b !== "function" && b !== null)
            throw new TypeError("Class extends value " + String(b) + " is not a constructor or null");
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
import EventEmitter from 'eventemitter3';
/**
 * @typedef {Object} SimpleLabelOptions
 * @property {number} [fontSize] Text size, in pixels. Default `13`.
 * @property {Array.<number>} [offset] Simpel Label offset, units in  `ems`. Defaults to `[0, 0]`.
 * @property {string} [anchor] Anchor. Possible values: `center`, `left`, `right`, `top`, `bottom`, `top-left`, `top-right`, `bottom-left`, `bottom-right`. Defaults `top`
 * @property {string} [justify] Justify: Possible values: `auto`, `left`, `center`, `right`. Defaults `center`
 * @property {string} text Simple Label text (with no background).
 * @property {boolean} visible Visible. Show or hide text.
 * @property {string} [textColor] Text color. Default `#000000`.
 * @property {string} [textOpacity] Text opacity. Default `1`.
 * @property {string} [strokeColor] Stroke color. Default `#ffffff`.
 * @property {number} [strokeWidth] Stroke width. Default `2`.
 * @property {number} [textRotation] Text rotation. Default `0`.
 */
/**
 * @classdesc
 * <b>Javascript ES5</b>
 * ```js
 * const simpleLabel = new cercaliagl.SimpleLabel({ ... });
 * ```
 *
 * Create a marker text. This text is rendered on `<canvas/>` via webgl. It is not possible to edit via CSS classes.<br/>
 * <br/>
 * Example usage:<br/>
 *
 * ```js
 * const simpleLabel = new cercaliagl.SimpleLabe({
 *   text: '
 * });
 * ```
 * @api
 */
var SimpleLabel = /** @class */ (function (_super) {
    __extends(SimpleLabel, _super);
    /**
     * @constructor
     * @param {SimpleLabelOptions} options Options
     * @api
     */
    function SimpleLabel(options) {
        var _this = _super.call(this) || this;
        /**
         * @private
         * @type {number}
         */
        _this.fontSize_ = options.fontSize !== undefined ? options.fontSize : 11;
        /**
         * @private
         * @type {Array.<number>}
         */
        _this.offset_ = options.offset !== undefined ? options.offset : [0, 0];
        /**
         * @private
         * @type {string}
         */
        _this.text_ = options.text !== undefined ? options.text : 'SimpleLabel?';
        /**
         * This property is used to store the text if it's hidden
         * @private
         * @type {string}
         */
        _this.textAux_ = _this.text_;
        /**
         * Text color
         * @private
         * @type {string}
         */
        _this.textColor_ = options.textColor !== undefined ? options.textColor : '#000000';
        /**
         * Stroke color
         * @private
         * @type {string}
         */
        _this.strokeColor_ = options.strokeColor !== undefined ? options.strokeColor : '#ffffff';
        /**
         * Stroke width
         * @private
         * @type {number}
         */
        _this.textOpacity_ = options.textOpacity !== undefined ? options.textOpacity : 1;
        /**
         * Stroke width
         * @private
         * @type {number}
         */
        _this.strokeWidth_ = options.strokeWidth !== undefined ? options.strokeWidth : 2;
        /**
         * Text rotation
         * @private
         * @type {number}
         */
        _this.textRotation_ = options.textRotation !== undefined ? options.textRotation : 0;
        /**
         * @private
         * @type {boolean}
         */
        _this.visible_ = options.visible !== undefined ? options.visible : true;
        /**
         * @private
         * @type {boolean}
         */
        _this.anchor_ = options.anchor !== undefined ? options.anchor : 'top';
        /**
         * @private
         * @type {boolean}
         */
        _this.justify_ = options.justify !== undefined ? options.justify : 'center';
        /**
         * @private
         * @type {import("./Marker").js}
         */
        _this.marker_ = null;
        if (!_this.visible_) {
            _this.setVisible(_this.visible_);
        }
        return _this;
    }
    /**
     * Change the text size in pixels. If have an assigned marker updates it
     * @param {number} fontSize Font size in pixels.
     * @api
     */
    SimpleLabel.prototype.setFontSize = function (fontSize) {
        this.fontSize_ = fontSize;
        this.emit('change');
    };
    /**
     * Returns text font
     * @return {number} Font size in pixels.
     * @api
     */
    SimpleLabel.prototype.getFontSize = function () {
        return this.fontSize_;
    };
    /**
     * Change displacement on the horizontal axis. If have an assigned marker updates it
     * @param {Array.<number>} offset Offset `[x,y]`.
     * @api
     */
    SimpleLabel.prototype.setOffset = function (offset) {
        this.offset_ = offset;
        this.emit('change');
    };
    /**
     * Returns offset.
     * @return {Array.<number>} Offset `[x,y]`.
     * @api
     */
    SimpleLabel.prototype.getOffset = function () {
        return this.offset_;
    };
    /**
     * Change the text passed by parameter. If have an assigned marker updates it
     * @param {string} text Text to put to simple label.
     * @api
     */
    SimpleLabel.prototype.setText = function (text) {
        this.text_ = text;
        this.textAux_ = text;
        this.emit('change');
    };
    /**
     * Get simple label text
     * @param {boolean} ignoreVisibility Ignore visibility
     * @return {string} Text.
     * @api
     */
    SimpleLabel.prototype.getText = function (ignoreVisibility) {
        if (ignoreVisibility === void 0) { ignoreVisibility = false; }
        return ignoreVisibility ? this.textAux_ : this.text_;
    };
    /**
     * Change simple label opacity. If have an assigned marker updates it
     * @param {number} textOpacity Text opacity
     * @api
    */
    SimpleLabel.prototype.setTextOpacity = function (textOpacity) {
        this.textOpacity_ = textOpacity;
        this.emit('change');
    };
    /**
     * Get simple label text opacity
     * @return {number} Text opacity
     * @api
    */
    SimpleLabel.prototype.getTextOpacity = function () {
        return this.textOpacity_;
    };
    /**
     * Get simple label anchor
     * @return {string} Anchor
     * @api
     */
    SimpleLabel.prototype.getAnchor = function () {
        return this.anchor_;
    };
    /**
     * Get simple label color
     * @return {string} Color
     * @api
     */
    SimpleLabel.prototype.getTextColor = function () {
        return this.textColor_;
    };
    /**
     * Get simple label stroke color
     * @return {string} strokeColor
     * @api
     */
    SimpleLabel.prototype.getStrokeColor = function () {
        return this.strokeColor_;
    };
    /**
     * Get simple label stroke width
     * @return {number} strokeWidth
     * @api
     */
    SimpleLabel.prototype.getStrokeWidth = function () {
        return this.strokeWidth_;
    };
    /**
     * Get simple label text rotation
     * @return {number} Text rotation
     * @api
     */
    SimpleLabel.prototype.getTextRotation = function () {
        return this.textRotation_;
    };
    /**
     * Get simple label justify
     * @return {string} Justify
     * @api
     */
    SimpleLabel.prototype.getJustify = function () {
        return this.justify_;
    };
    /**
     * Assign a marker to this SimpleLabel
     * @param {import('./BaseFeature').default} feature FEature.
     */
    SimpleLabel.prototype.setBaseFeature = function (feature) {
        this.feature_ = feature;
    };
    /**
     * Return if simple label is visible
     * @return {boolean}
     * @api
     */
    SimpleLabel.prototype.isVisible = function () {
        return this.visible_;
    };
    /**
     * Show or hide simple label text
     * @param {boolean} visible Visible
     * @api
     */
    SimpleLabel.prototype.setVisible = function (visible) {
        this.visible_ = visible;
        if (visible) {
            this.text_ = this.textAux_;
        }
        else {
            this.text_ = '';
        }
        this.emit('change');
    };
    return SimpleLabel;
}(EventEmitter));
export default SimpleLabel;
//# sourceMappingURL=SimpleLabel.js.map